import {startOfDay, endOfDay} from 'date-fns';
import moment from 'moment';
import {Authorization} from 'src/models';

/**
 * Extracts all unique CPT codes from claim sessions
 */
export const getClaimCptCodes = (sessionsData: any[]): string[] => {
  const cptCodes = new Set<string>();
  sessionsData.forEach(session => {
    session.services?.forEach((service: any) => {
      if (service.cpt) {
        cptCodes.add(service.cpt);
      }
    });
  });
  return Array.from(cptCodes);
};

/**
 * Gets the min and max service dates from claim sessions
 */
export const getServiceDateRange = (
  sessionsData: any[],
): {minDate: Date | null; maxDate: Date | null} => {
  let minDate: Date | null = null;
  let maxDate: Date | null = null;

  sessionsData.forEach(session => {
    if (session.startDate) {
      const startDate = new Date(session.startDate);
      if (!minDate || startDate < minDate) {
        minDate = startDate;
      }
    }
    if (session.endDate) {
      const endDate = new Date(session.endDate);
      if (!maxDate || endDate > maxDate) {
        maxDate = endDate;
      }
    }
  });

  return {minDate, maxDate};
};

/**
 * Checks if an authorization has units available for a specific CPT code
 */
export const authHasCptUnits = (
  auth: Authorization,
  cptCode: string,
): boolean => {
  // Map CPT code to authorization field name
  const cptFieldMap: {[key: string]: string} = {
    '97151': 'cpt97151',
    '97152': 'cpt97152',
    '97153': 'cpt97153',
    '97154': 'cpt97154',
    '97155': 'cpt97155',
    '97156': 'cpt97156',
    '97157': 'cpt97157',
    '97158': 'cpt97158',
    '0373T': 'cpt0373T',
    '0362T': 'cpt0362T',
    T1024: 'cptT1024',
    '92507': 'cpt92507',
    '92508': 'cpt92508',
    '92526': 'cpt92526',
    '92609': 'cpt92609',
    '92522': 'cpt92522',
    '92523': 'cpt92523',
    '92610': 'cpt92610',
    H2019: 'cptH2019',
    T2024: 'cptT2024',
    H0032: 'cptH0032',
    '90791': 'cpt90791',
    '90837': 'cpt90837',
    H0031: 'cptH0031',
    H2012: 'cptH2012',
    S5111: 'cptS5111',
    H2014: 'cptH2014',
    H0046: 'cptH0046',
    '612': 'cpt612',
    '616': 'cpt616',
    '805': 'cpt805',
    '615': 'cpt615',
    '620': 'cpt620',
  };

  const fieldName = cptFieldMap[cptCode];
  if (!fieldName) {
    return false;
  }

  // Check if the authorization has this field and it has units > 0
  const units = auth[fieldName as keyof Authorization];
  return units != null && Number(units) > 0;
};

/**
 * Scores an authorization based on how well it matches the claim's CPT codes and dates
 *
 * Scoring algorithm:
 * - Base score: (CPT codes with units / Total CPT codes) * 100
 * - Bonus: +10 if all service dates fall within auth date range
 * - Returns 0 if the auth has no units for any of the claim's CPT codes
 * - Returns 0 if any service dates fall outside auth date range
 */
export const scoreAuthorization = (
  auth: Authorization,
  cptCodes: string[],
  dateRange: {minDate: Date | null; maxDate: Date | null},
): number => {
  if (!cptCodes.length) {
    return 0;
  }

  // Calculate CPT coverage score
  const coveredCpts = cptCodes.filter(cpt => authHasCptUnits(auth, cpt));
  const cptCoverageScore = (coveredCpts.length / cptCodes.length) * 100;

  // If the auth has no units for any of the claim's CPT codes, return 0
  // This prevents selecting an auth that can't actually be used for billing
  if (cptCoverageScore === 0) {
    return 0;
  }

  // Check date coverage
  let dateBonus = 0;
  if (
    dateRange.minDate &&
    dateRange.maxDate &&
    auth.startDate &&
    auth.endDate
  ) {
    const authStart = startOfDay(auth.startDate);
    const authEnd = endOfDay(auth.endDate);
    const allDatesWithinRange =
      (moment(dateRange.minDate).isBetween(authStart, authEnd) ||
        moment(dateRange.minDate).isSame(authStart, 'day')) &&
      (moment(dateRange.maxDate).isBetween(authStart, authEnd) ||
        moment(dateRange.maxDate).isSame(authEnd, 'day'));

    if (allDatesWithinRange) {
      dateBonus = 10;
    } else {
      // Disqualify completely if dates are outside range
      return 0;
    }
  }

  return cptCoverageScore + dateBonus;
};

/**
 * Finds the best matching authorization for a claim
 *
 * @param authorizations - Array of available authorizations
 * @param sessions - Claim sessions with service lines
 * @returns The best matching authorization or null if none found
 */
export const findBestAuthorizationMatch = (
  authorizations: Authorization[],
  sessions: any[],
): Authorization | null => {
  if (!authorizations.length || !sessions.length) {
    return null;
  }

  const cptCodes = getClaimCptCodes(sessions);
  const dateRange = getServiceDateRange(sessions);

  if (cptCodes.length === 0) {
    return null;
  }

  // Score all authorizations
  const scoredAuths = authorizations.map(auth => ({
    auth,
    score: scoreAuthorization(auth, cptCodes, dateRange),
  }));

  // Sort by score (descending), then by end date (descending) for tie-breaking
  scoredAuths.sort((a, b) => {
    if (b.score !== a.score) {
      return b.score - a.score;
    }
    // Tie-breaker: use latest end date
    const endDateA = a.auth.endDate ? new Date(a.auth.endDate).getTime() : 0;
    const endDateB = b.auth.endDate ? new Date(b.auth.endDate).getTime() : 0;
    return endDateB - endDateA;
  });

  // Return the best authorization if it has a positive score
  if (scoredAuths.length > 0 && scoredAuths[0].score > 0) {
    return scoredAuths[0].auth;
  }

  return null;
};
